import React from "react";
import { BaseEdge, EdgeLabelRenderer, getBezierPath, useReactFlow } from "@xyflow/react";
import "./InteractiveEdge.scss";

/**
 * Interactive Edge Component with Add and Delete functionality
 * @param {string} id - Edge identifier
 * @param {number} sourceX - Source X coordinate
 * @param {number} sourceY - Source Y coordinate
 * @param {number} targetX - Target X coordinate
 * @param {number} targetY - Target Y coordinate
 * @param {string} sourcePosition - Source position
 * @param {string} targetPosition - Target position
 * @param {Object} style - Edge styling
 * @param {string} markerEnd - Marker end identifier
 * @param {boolean} selected - Whether edge is selected
 * @param {string} source - Source node ID
 * @param {string} target - Target node ID
 * @param {string} sourceHandle - Source handle identifier
 * @param {Object} data - Edge data with handlers
 */
const InteractiveEdge = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  markerEnd,
  selected,
  source,
  target,
  sourceHandle,
  data,
}) => {
  const { setEdges } = useReactFlow();

  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  /**
   * Handles add node button click
   */
  const handleAddNode = (event) => {
    event.stopPropagation();
    const handleFromData = data?.sourceHandle;
    if (data?.onAddNode) {
      const handle = handleFromData || "bottom";
      const rect = event.currentTarget.getBoundingClientRect();
      data.onAddNode(source, target, handle, {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
      });
    }
  };

  /**
   * Handles delete edge button click
   */
  const handleDeleteEdge = async (event) => {
    event.stopPropagation();
    if (data?.onDeleteEdge) {
      await data.onDeleteEdge(id, source, target);
    }
  };

  return (
    <>
      <BaseEdge path={edgePath} markerEnd={markerEnd} />

      <path d={edgePath} fill='none' stroke='transparent' strokeWidth={20} className='interactive-edge-hitbox' />

      {selected && !data?.isReadOnly && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: "absolute",
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
              pointerEvents: "all",
            }}
            className='edge-button-group'>
            <button onClick={handleAddNode} className='edge-btn edge-btn-add' title='Add node'>
              +
            </button>
            <button onClick={handleDeleteEdge} className='edge-btn edge-btn-delete' title='Delete edge'>
              ×
            </button>
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
};

export default InteractiveEdge;
